import { useState } from "react";
import axios from "axios";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const RATING_LABELS = {
  1: "Not great",
  2: "Could be better",
  3: "It's okay",
  4: "Pretty good",
  5: "Loved it",
};

const FeedbackDialog = ({ open, onOpenChange, placement, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setRating(0);
    setHoverRating(0);
    setComment("");
  };

  const handleOpenChange = (next) => {
    if (submitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!rating || !placement) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        placement_id: placement.placement_id,
        rating,
        comment: comment.trim() || null,
      });
      toast.success("Thanks for the feedback!");
      reset();
      onOpenChange(false);
      onSubmitted?.();
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to submit feedback";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">How was your experience?</DialogTitle>
          <DialogDescription>
            Your <span className="font-medium text-foreground">{placement?.company}</span> submission was just verified.
            Tell us how the process felt — it helps us improve.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-2 py-2">
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onMouseEnter={() => setHoverRating(value)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(value)}
                disabled={submitting}
                className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring rounded"
                aria-label={`${value} star${value > 1 ? "s" : ""}`}
              >
                <Star
                  className={`w-9 h-9 transition-colors ${
                    value <= displayRating
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-slate-300"
                  }`}
                />
              </button>
            ))}
          </div>
          <div className="h-5 text-sm text-muted-foreground">
            {displayRating ? RATING_LABELS[displayRating] : "Tap a star to rate"}
          </div>
        </div>

        <Textarea
          placeholder="Anything you'd like to share? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={submitting}
          maxLength={500}
          rows={4}
          className="resize-none"
        />
        <div className="text-[11px] text-muted-foreground text-right -mt-2">
          {comment.length}/500
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Maybe later
          </Button>
          <Button onClick={handleSubmit} disabled={!rating || submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
